import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border px-2.5 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1.5 [&>svg]:size-3 [&>svg]:pointer-events-none transition-colors",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/80",
        soft: "border-transparent bg-primary-soft text-primary",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90",
        outline: "text-foreground border-border [a&]:hover:bg-accent",
        "status-success": "border-transparent bg-status-success-bg text-status-success-text",
        "status-warning": "border-transparent bg-status-warning-bg text-status-warning-text",
        "status-danger": "border-transparent bg-status-danger-bg text-status-danger-text",
        "status-info": "border-transparent bg-status-info-bg text-status-info-text",
        "status-neutral": "border-transparent bg-status-neutral-bg text-status-neutral-text",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

const DOT_CLASS: Partial<Record<BadgeVariant, string>> = {
  "status-success": "bg-status-success-dot",
  "status-warning": "bg-status-warning-dot",
  "status-danger": "bg-status-danger-dot",
  "status-info": "bg-status-info-dot",
  "status-neutral": "bg-status-neutral-dot",
};

function Badge({
  className,
  variant,
  asChild = false,
  dot = false,
  pulse = false,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    /** Leading status dot — reads at a glance even for colour-blind users when paired with the label. */
    dot?: boolean;
    /** Animated ring on the dot, for in-progress states. */
    pulse?: boolean;
  }) {
  const Comp = asChild ? Slot : "span";
  const dotClass = variant ? DOT_CLASS[variant] : undefined;
  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && dotClass && (
        <span
          aria-hidden="true"
          className={cn("size-1.5 rounded-full", dotClass, pulse && "animate-pulse-dot")}
        />
      )}
      {children}
    </Comp>
  );
}

export { Badge, badgeVariants };
export type { BadgeVariant };
